import { Field as FinalField, useForm } from 'react-final-form';
import type { FieldState } from 'final-form';
import { LUISelectProps, OptionType, Select, ValueType } from '@lendi-ui/dropdown';
import Field from '@lendi-ui/field';

import { useUpdatedRef } from '@funnels/hooks';
import { getAriaLabelById, getSelectedValue, validateNotEmpty } from '@funnels/utils';

import { FieldLabel } from '../_common/FieldLabel';
import { FieldWrapper } from '../_common/style';
import { CommonFieldProps } from '../_common/types';

/**
 * https://final-form.org/docs/react-final-form/types/FieldProps
 * Please set a dynamic <key> prop for this component if you have a dynamic <isDisable> or <isOptional>
 */
export interface DropdownFieldProps extends CommonFieldProps, Omit<LUISelectProps, 'name' | 'onChange'> {
  className?: string;
  defaultValue?: string;
  format?: (value: any, name: string) => any;
  onChange?: (value: string[]) => boolean | void;
  parse?: (value: any, name: string) => any;
  placeHolder?: string;
  shouldAnchorList?: boolean;
}

const DropdownField = (props: DropdownFieldProps) => {
  const {
    className,
    defaultValue,
    disableFailMessage = false,
    format,
    isDisabled,
    isOptional,
    label,
    labelAfter,
    name,
    onChange,
    options,
    parse,
    placeHolder,
    subLabel,
    validate: validateFn,
    validationFailMessage,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    shouldAnchorList,
    isSearchable = true,
    ...otherProps
  } = props;
  const form = useForm();

  const handleChange = (values: ValueType<OptionType>) => {
    let selected: string | string[] = otherProps.isMultiple ? [] : '';
    if (values) {
      if (otherProps.isMultiple && Array.isArray(values)) {
        selected = values.map((v) => v.value);
      } else {
        selected = Array.isArray(values) ? values[0].value : (values as any).value;
      }
    }
    if (onChange) {
      const onChangeValue = Array.isArray(selected) ? selected : [selected];
      //provide a way to skip a delayed assignment.
      //by the consideration of backward compatibility, only return true can skip the assignment
      if (onChange(onChangeValue)) return;
    }

    form.change(name, selected);
  };

  /**
   * https://final-form.org/docs/react-final-form/types/FieldProps
   * Please read <validate> part of this doc. It's not totally related to this topic, but provide an idea on why this happened.
   * <useRef> will generate a memorize object when the component mounting and release it when the component unmounting.
   * This means the <validate> will not be changed after component mounted. Anytime you reference the current values are the initial values.
   * Props changes outside will not cause unmount, so the <validate> will never be changed.
   * Therefore, we need to set dynamic <key> for this whole component to force it totally destroy and re-register to form
   */
  const validationRef = useUpdatedRef({ isDisabled, isOptional });
  const validate = (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (validationRef.current.isDisabled) {
      return;
    }
    if (validateFn) {
      return validateFn(value, allValues, fieldMeta);
    } else {
      return validateNotEmpty(validationFailMessage, validationRef.current.isOptional)(value, allValues, fieldMeta);
    }
  };

  const fieldProps = {
    name,
    format,
    parse,
    validate,
    defaultValue,
  };

  const luiProps: any = {
    ...otherProps,
    isDisabled,
  };

  if (shouldAnchorList) {
    luiProps.menuPortalTarget = document.querySelector('body');
    luiProps.menuPosition = 'absolute';
  }

  return (
    <FieldWrapper className={className}>
      <FieldLabel
        label={label}
        labelAfter={labelAfter}
        subLabel={subLabel}
        idPostfix={name}
        isOptional={validationRef.current.isOptional}
      />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <Field {...meta} data-component={name} error={disableFailMessage ? undefined : meta.error}>
            <Select
              {...luiProps}
              name={input.name}
              className={className}
              options={options}
              size="md"
              value={getSelectedValue(options, input.value)}
              onChangeItem={handleChange}
              isSearchable={isSearchable}
              isFullWidth
              isError={!!(meta.error && meta.touched)}
              isOptionsOverflow
              aria-labelledby={getAriaLabelById('label', label ? name : undefined, ariaLabelledby)}
              aria-describedby={getAriaLabelById('description', subLabel ? name : undefined, ariaDescribedby)}
              aria-invalid={disableFailMessage ? false : meta.error && meta.touched}
            />
          </Field>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default DropdownField;
